import { applySeo } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useCases } from "@/lib/cases";
import { ExternalLink, Newspaper, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";
import casesBg from "@/assets/cases-bg.mp4.asset.json";

export const Route = createFileRoute("/services/casos")({
  component: Casos,
  head: (ctx) => applySeo(ctx.matches, ((_c: unknown) => ({
    meta: [
      { title: "Casos en los medios — RR Lex Juristas" },
      { name: "description", content: "Procedimientos del despacho RR Lex Juristas con cobertura en prensa nacional. Identidad de clientes siempre protegida." },
      { property: "og:title", content: "Casos en los medios — RR Lex Juristas" },
      { property: "og:description", content: "Elegidos para defender lo más difícil: cobertura mediática y resultados." },
      { property: "og:url", content: "https://rrlexjuristas.com/services/casos" },
    ],
    links: [{ rel: "canonical", href: "https://rrlexjuristas.com/services/casos" }],
  }))(ctx)),
});

function Casos() {
  const { tk } = useT();
  const cases = useCases();

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-gold/15">
        <video
          src={casesBg.url}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
        />
        <div className="absolute inset-0 bg-background/80" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("cases.kicker", "Casos en los medios")}
          </span>
          <h1 className="font-serif text-4xl md:text-6xl mt-5 max-w-4xl mx-auto">
            {tk("cases.title.a", "Elegidos para defender lo ")}
            <em className="not-italic gold-gradient">{tk("cases.title.b", "más difícil")}</em>
          </h1>
          <p className="mt-6 text-foreground/80 max-w-2xl mx-auto leading-relaxed">
            {tk(
              "cases.intro",
              "RR Lex Juristas ha participado en más de 20 procedimientos con cobertura en prensa nacional. Compartimos aquí algunos de los casos más conocidos, siempre respetando el secreto profesional y la dignidad de todas las partes.",
            )}
          </p>
          <div className="mt-10 inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-foreground/70 border border-gold/40 bg-background/40 backdrop-blur-sm rounded-full px-5 py-2.5">
            <ShieldCheck size={14} className="text-gold" />
            {tk("cases.disclaimer", "Información pública. Identidades de los clientes protegidas.")}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 py-20 space-y-16">
          {cases.map((c, idx) => (
            <article
              key={c.slug}
              id={c.slug}
              className="scroll-mt-32 rounded-2xl border border-gold/20 bg-card/30 p-8 md:p-12"
            >
              <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-gold/80">
                <Newspaper size={14} />
                <span>{c.area}</span>
                <span className="text-muted-foreground/60">· {c.year}</span>
                <span className="ml-auto text-muted-foreground/60">
                  #{String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-5 leading-snug">{c.headline}</h2>
              <div className="gold-divider my-6 max-w-[80px]" />
              <p className="text-foreground/80 leading-relaxed">{c.summary}</p>
              <div className="mt-6 text-xs tracking-[0.25em] uppercase text-gold/80">{c.role}</div>

              <div className="mt-8">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                  {tk("cases.coverage", "Cobertura en prensa")}
                </div>
                <ul className="space-y-2">
                  {c.mentions.map((m) => (
                    <li key={m.url}>
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="group inline-flex items-center gap-3 text-sm text-foreground/80 hover:text-gold transition-colors"
                      >
                        <ExternalLink
                          size={14}
                          className="text-gold/70 group-hover:text-gold transition-colors"
                        />
                        <span className="border-b border-gold/20 group-hover:border-gold pb-0.5">
                          {m.outlet}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}

          <p className="text-center text-sm text-muted-foreground italic max-w-2xl mx-auto">
            {tk(
              "cases.more",
              "Y muchos otros procedimientos que, por respeto a la confidencialidad de nuestros clientes, no se publican.",
            )}
          </p>
        </div>
      </section>
    </Layout>
  );
}
